import { SecondFromMidnight, TimeSlot } from "@cervval.js/calendars/src/calendars.definition";
import { StartAndFinish } from "@cervval.js/calendars/src/calendars.helper";
import { objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { durationValue, TsDuration, UnixTimestamp, valueOf } from "@cervval.js/common/src/tools/timestamps.tools";
import { BookingException } from "../data/booking.dto";

/**
 * Flatten the timeshifts and parse the overlap graph to detect new merge ranges
 * eg. ^ : start bloc          $: end bloc
 * 
 * ----------
 *     ----------                                 ----
 *                 ----------  ----x----     ------------
 * 
 *     2____2                                    2___2
 *     |    |                                    |   |
 * 1^__1    1____1 1^_______1  1^__1___1    1^___1   1__1
 * |             | |        |  |       |    |           |
 * 0            $0_0       $0__0  (0) $0____0           0$
 */
export function mergeTimeSlots(timeslots: TimeSlot[]): TimeSlot[] {
    timeslots = [...timeslots];
    const overlapGraph: Record<number, number> = {};
    for (const timeshift of timeslots) {
        overlapGraph[timeshift.start] = (overlapGraph[timeshift.start] ?? 0) + 1;
        overlapGraph[timeshift.finish] = (overlapGraph[timeshift.finish] ?? 0) - 1;
    }
    const timeline = objectKeys(overlapGraph).map(v => Number(v)).sort((a, b) => {
        return a - b;
    });
    const mergedTimeShifts: TimeSlot[] = [];
    let bufferTimeShift: Partial<TimeSlot> | undefined;
    let heigth = 0;
    for (const keyframe of timeline) {
        heigth += overlapGraph[keyframe];
        // If a timeshift is bufferred and timeline cross a 0 : push the buffered timeshift
        if (heigth <= 0 && bufferTimeShift) {
            bufferTimeShift.finish = keyframe as SecondFromMidnight;
            mergedTimeShifts.push(bufferTimeShift as TimeSlot);
            bufferTimeShift = undefined;
        } else {
            // If there is no buffered timeshift and timeline is above 0 : create a new timeshift
            if (bufferTimeShift === undefined) {
                bufferTimeShift = {
                    start: keyframe as SecondFromMidnight
                };
            }
        }
    }
    return mergedTimeShifts;
}

/** Get a new TsDuration with the added minutes */
export function addDurations(a: TsDuration, b: TsDuration): TsDuration {
    return { second: durationValue(a, "second") + durationValue(b, "second") };
}

/**
 * Get closest minutes regarding an interval
 *
 * @param {number} minutes to get closest minutes
 * @param {number} minuteInterval used to split minutes sample
 * @param {number} start
 * @param {number} finish
 * @returns {(number | undefined)} undefined if minutes is out of range ( not between start and finish)
 */
export function getClosestMinutes(minutes: number, minuteInterval: number, start: number, finish: number): number | undefined {
    if (minutes < start || minutes > finish) {
        return undefined;
    }
    const range = finish - start;
    const intervalNumber = range / minuteInterval;
    return start + Math.round(Math.floor((minutes - start) / minuteInterval) / intervalNumber * range);
}

/**
 * Filter by date intervale (including start, excluding finish)
 * @param toFilter 
 * @param filterWith 
 * @returns 
 */
export function filterByDateInterval<K extends StartAndFinish, L extends StartAndFinish>(toFilter: K[], filterWith: L): K[] {
    return toFilter.filter(v => {
        return v.start >= filterWith.start && v.start < filterWith.finish || v.finish >= filterWith.start && v.finish < filterWith.finish || v.start <= filterWith.start && v.finish > filterWith.finish;
    });
}

/** */
export function getActiveException(at: UnixTimestamp, exceptions: BookingException[]): BookingException | undefined {
    const overlapping = filterByDateInterval(exceptions, { start: at, finish: at });
    overlapping.sort((a, b) => {
        const passStart = valueOf(b.start) - valueOf(a.start);
        if (passStart === 0) {
            return valueOf(a.finish) - valueOf(b.finish);
        }
        return passStart;
    });
    return overlapping[0];
}